import { useSessionStore } from "@auth/sessionStore";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faUser, faUserGroup } from "@fortawesome/pro-solid-svg-icons";
import type { Task } from "@tasks/tasksClient";
import { useClinicRoles } from "@users/rolesQueries";
import { useClinicUsers } from "@users/userQueries";

/** A task's owner, resolved to something a surface can write down. */
export interface TaskAssignee {
  name: string;
  /** Users and roles must never read as the same thing: "Sarah" is not "Front desk". */
  kind: "user" | "role";
  icon: IconDefinition;
}

/**
 * Who owns a task: the assigned user, or the assigned role when no user is set
 * (WEB-3198). Name resolution is best-effort from the clinic users/roles lists,
 * so this is `null` until they load, and `null` for an unassigned task, so a
 * surface renders nothing rather than a nameless owner.
 *
 * The lookup lives here because two presentations need the same answer (the
 * legacy badge and the index card's filing line), and only the presentation
 * should differ between them.
 */
export const useTaskAssignee = (task: Task): TaskAssignee | null => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: users } = useClinicUsers(sessionKey);
  const { data: roles } = useClinicRoles(sessionKey);

  // A task with an assigned user is that user's, full stop: an unresolved name
  // stays unresolved rather than falling through to the role, which would put
  // someone else's owner on the card.
  const assignee: { name: string | undefined; kind: "user" | "role" } | null =
    task.assigneeUserId
      ? {
          name: users?.find((u) => u.id === task.assigneeUserId)?.displayName,
          kind: "user",
        }
      : task.assignedRoleId
        ? {
            name: roles?.find((r) => r.id === task.assignedRoleId)?.name,
            kind: "role",
          }
        : null;

  if (!assignee?.name) return null;

  return {
    name: assignee.name,
    kind: assignee.kind,
    icon: assignee.kind === "user" ? faUser : faUserGroup,
  };
};
